/* eslint-disable no-restricted-syntax, no-await-in-loop, no-case-declarations */
import * as assetsApiService from '@/services/assets-api/assetsApiService';
import {
  AssetsNamespaces,
  AssetsResponse,
  AssetsResponseBulk,
  AssetsSubNamespaces,
} from '@/services/assets-api/assetsClient';
import { v4 as uuidv4 } from 'uuid';
import { fileNameToId, getPhotoImageAndDrawingFiles } from '@/services/assets-api/assets-helper';

export enum AssetsPhotoActions {
  ADD_IMAGE = 'add_image',
  ADD_IMAGE_WITH_DRAWING = 'add_image_with_drawing',
  UPDATE_DRAWING = 'update_drawing',
  DELETE_IMAGE = 'delete_image',
  DELETE_DRAWING = 'delete_drawing',
  ADD_IMAGE_TO_QUESTION = 'add_image_to_question'
}

export interface AssetsPhotoStub {
  loading?: boolean;
  image?: string | null;
  drawing?: string | null;
  thumbnail?: string | null;
  width: number | null;
  height: number | null;
  image_id: string | null;
  drawing_id: string | null;
  thumbnail_id: string | null;
  uuid: string;
  action?: AssetsPhotoActions;
}

export const getPhotoStub = (): AssetsPhotoStub => ({
  loading: true,
  image: null,
  image_id: null,
  width: null,
  height: null,
  drawing: null,
  drawing_id: null,
  thumbnail: null,
  thumbnail_id: null,
  uuid: uuidv4(),
});

export interface AssetsPhotoStubActionable extends AssetsPhotoStub {
  image_id_delete?: string | null;
  drawing_id_delete?: string | null;
  image_query?: File;
}

export const processPhoto = async (photo: AssetsPhotoStubActionable) => {
  switch (photo.action) {
    case AssetsPhotoActions.DELETE_IMAGE:
      if (photo.image_id_delete || photo.drawing_id_delete) {
        await assetsApiService.removeImageBulk(
          [photo.image_id_delete, photo.drawing_id_delete],
        );
      }

      return null;
    case AssetsPhotoActions.DELETE_DRAWING:
      if (photo.drawing_id_delete) {
        await assetsApiService.removeImage(photo.drawing_id_delete);
      }
      delete photo.drawing_id_delete;
      break;
    case AssetsPhotoActions.UPDATE_DRAWING:
      const drawingUploadResponse = await assetsApiService.uploadBase64Image({
        file: photo.drawing!,
        namespace: AssetsNamespaces.issues,
        subnamespace: AssetsSubNamespaces.images,
      });

      if (!drawingUploadResponse) {
        throw new Error();
      }

      photo.drawing_id = (drawingUploadResponse as AssetsResponse).id!;

      break;
    case AssetsPhotoActions.ADD_IMAGE:
      const imageUploadResponse = await assetsApiService.uploadBase64Image(
        {
          file: photo.image!,
          namespace: AssetsNamespaces.inspections,
          subnamespace: AssetsSubNamespaces.questions,
        },
      );

      if (!imageUploadResponse) {
        throw new Error();
      }

      photo.image_id = (imageUploadResponse as AssetsResponse).id!;

      break;
    case AssetsPhotoActions.ADD_IMAGE_WITH_DRAWING:
      const files = getPhotoImageAndDrawingFiles(photo);
      if (files.length === 0) {
        break;
      }

      const imageAndDrawingUploadResult = await assetsApiService.uploadBase64ImageMany(
        {
          files,
          namespace: AssetsNamespaces.inspections,
          subnamespace: AssetsSubNamespaces.questions,
        },
      );

      if (!imageAndDrawingUploadResult) {
        throw new Error();
      }

      const fileNameToIdMap = fileNameToId(imageAndDrawingUploadResult as AssetsResponseBulk);

      photo.image_id = fileNameToIdMap.image ?? null;
      photo.drawing_id = fileNameToIdMap.drawing ?? null;

      break;
    case AssetsPhotoActions.ADD_IMAGE_TO_QUESTION:
      const imageToQuestionResponse = await assetsApiService.uploadBase64Image(
        {
          file: photo.image!,
          namespace: AssetsNamespaces.checklists,
          subnamespace: AssetsSubNamespaces.questions,
        },
      );

      if (!imageToQuestionResponse) {
        throw new Error();
      }

      photo.image_id = (imageToQuestionResponse as AssetsResponse).id!;

      break;
    default:
  }

  delete photo.action;
  delete photo.drawing;
  delete photo.image;
  delete photo.thumbnail;
  return photo;
};
